import { Icon } from '@carrent/ui'

/**
 * Дерево оргструктуры.
 *
 * Три яруса — три визуальных веса (правило брифа «не делать все
 * карточки одинаковыми»): владелец — крупная карточка с акцентной
 * рамкой; отделы — компактные пилюли без метрик; регионы — рабочие
 * карточки со статусом маршрутизации и счётчиками.
 *
 * Линии — чистый CSS (псевдоэлементы), без библиотек: уровней три,
 * узлов десятки, зависимость не окупается.
 */

const ROUTING_LABEL = {
  ok:          'Ответственный назначен',
  substituted: 'Действует замещение',
  escalated:   'Согласования уходят владельцу',
}

function initials(name) {
  return (name || '')
    .split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase()
}

export function PersonAvatar({ name, size = 'md' }) {
  return (
    <span className={`org-avatar org-avatar--${size}`} aria-hidden="true">
      {initials(name)}
    </span>
  )
}

function fmtDay(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

export function OwnerCard({ owner }) {
  if (!owner) return null
  return (
    <div className="org-owner" data-testid="org-owner">
      <PersonAvatar name={owner.full_name} size="lg" />
      <div>
        <div className="org-owner__name">{owner.full_name}</div>
        <div className="org-owner__role">Владелец компании</div>
      </div>
    </div>
  )
}

export function DepartmentPill({ dep, onClick }) {
  const count = dep.members_count ?? dep.members?.length ?? 0
  return (
    <button type="button" className="org-dep" onClick={onClick}>
      <span className="org-dep__icon"><Icon name={dep.icon || 'users'} size={16} /></span>
      <span>
        <span className="org-dep__name">{dep.name}</span>
        <span className="org-dep__count">
          {count} {plural(count, 'сотрудник', 'сотрудника', 'сотрудников')}
        </span>
      </span>
    </button>
  )
}

export function plural(n, one, few, many) {
  const m10 = n % 10, m100 = n % 100
  if (m10 === 1 && m100 !== 11) return one
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few
  return many
}

export function RegionCard({ node, onOpen }) {
  const res = node.responsible || {}
  const sub = res.substitution

  return (
    <div className={`org-region org-region--${node.routing}`}>
      <div className="org-region__head">
        <span
          className={`org-region__dot org-region__dot--${node.routing}`}
          title={ROUTING_LABEL[node.routing]}
        />
        <button
          type="button"
          className="org-region__name"
          onClick={() => onOpen(node)}
        >
          {node.name}
        </button>
        <button
          type="button"
          className="org-region__manage"
          title="Открыть регион"
          onClick={() => onOpen(node)}
        >
          <Icon name="expand" size={14} />
        </button>
      </div>

      <div className="org-region__resp">
        {res.user ? (
          <>
            <PersonAvatar name={res.user.full_name} size="sm" />
            <span className="org-region__resp-name">{res.user.full_name}</span>
          </>
        ) : (
          <span className="text-muted">Ответственный не назначен</span>
        )}
      </div>

      {node.routing === 'substituted' && sub && (
        <div className="org-region__badge org-region__badge--sub">
          Замещает {sub.principal?.full_name} до {fmtDay(sub.ends_at)}
        </div>
      )}
      {node.routing === 'escalated' && (
        <div className="org-region__badge org-region__badge--esc">
          Согласования уходят владельцу
        </div>
      )}

      <div className="org-region__counts">
        <div><b>{node.counts?.drivers ?? 0}</b><span>Водители</span></div>
        <div><b>{node.counts?.cars ?? 0}</b><span>Автомобили</span></div>
        <div><b>{node.counts?.tickets_open ?? 0}</b><span>Обращения</span></div>
      </div>

      {node.drivers_preview?.length > 0 && (
        <div className="org-region__drivers">
          {node.drivers_preview.slice(0, 5).map((d) => (
            <span key={d.id} title={d.full_name}>
              <PersonAvatar name={d.full_name} size="xs" />
            </span>
          ))}
          {node.counts?.drivers > 5 && (
            <span className="org-region__more">+{node.counts.drivers - 5}</span>
          )}
        </div>
      )}
    </div>
  )
}

export default function OrgTree({ data, onOpenRegion, onOpenDepartment }) {
  return (
    <div className="org-tree">
      <div className="org-tier org-tier--owner">
        <OwnerCard owner={data.owner} />
      </div>

      {data.departments?.length > 0 && (
        <div className="org-tier org-tier--deps">
          {data.departments.map((dep) => (
            <div className="org-tier__cell" key={dep.id}>
              <DepartmentPill dep={dep} onClick={() => onOpenDepartment(dep)} />
            </div>
          ))}
        </div>
      )}

      <div className="org-tier org-tier--regions">
        {data.regions.map((node) => (
          <div className="org-tier__cell" key={node.id}>
            <RegionCard node={node} onOpen={onOpenRegion} />
          </div>
        ))}
        {data.regions.length === 0 && (
          <p className="text-muted">Регионы ещё не созданы</p>
        )}
      </div>
    </div>
  )
}
